import { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Trash2, Wallet, X } from "lucide-react";
import { api, apiError } from "@/lib/api";

function InviteDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({
    email: "",
    full_name: "",
    password: "",
    account_type: "B",
    monthly_fee: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.email || !form.full_name || !form.password) {
      toast.error("Name, email and password are required");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        email: form.email.trim(),
        full_name: form.full_name.trim(),
        password: form.password,
        account_type: form.account_type,
      };
      if (form.account_type === "B" && form.monthly_fee) {
        payload.monthly_fee = Math.round(Number(form.monthly_fee) * 100);
      }
      await api.post("/admin/practitioners", payload);
      toast.success("Practitioner invited");
      onCreated();
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const field = "w-full rounded-xl border border-soma-border bg-soma-surface px-4 py-3 text-soma-on outline-none focus:border-soma-brand";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-md rounded-2xl border border-soma-border bg-soma-surface2 p-6" data-testid="invite-dialog">
        <div className="mb-5 flex items-center justify-between">
          <h3 className="font-display text-2xl text-soma-on">Invite Practitioner</h3>
          <button onClick={onClose} data-testid="invite-close" className="text-soma-muted hover:text-soma-on">
            <X size={22} />
          </button>
        </div>
        <div className="space-y-3">
          <input placeholder="Full name" className={field} value={form.full_name} data-testid="invite-name" onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
          <input placeholder="Email" type="email" className={field} value={form.email} data-testid="invite-email" onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <input placeholder="Temporary password" className={field} value={form.password} data-testid="invite-password" onChange={(e) => setForm({ ...form, password: e.target.value })} />
          <input placeholder="Monthly fee (USD, optional)" type="number" className={field} value={form.monthly_fee} data-testid="invite-fee" onChange={(e) => setForm({ ...form, monthly_fee: e.target.value })} />
          <p className="text-xs text-soma-muted">Flat monthly access · billed off-platform. No in-app payment.</p>
        </div>
        <button
          onClick={submit}
          disabled={busy}
          data-testid="invite-submit"
          className="mt-5 w-full rounded-xl bg-soma-brand py-3 font-semibold text-soma-surface hover:opacity-90 disabled:opacity-60"
        >
          {busy ? "Inviting…" : "Send invitation"}
        </button>
      </div>
    </div>
  );
}

function PayoutDialog({ practitioner, onClose, onSaved }) {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!amount || Number(amount) <= 0) {
      toast.error("Enter a payout amount");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/admin/practitioners/${practitioner.id}/payouts`, {
        amount: Math.round(Number(amount) * 100),
        note: note || undefined,
      });
      toast.success("Payout recorded");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const field = "w-full rounded-xl border border-soma-border bg-soma-surface px-4 py-3 text-soma-on outline-none focus:border-soma-brand";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-md rounded-2xl border border-soma-border bg-soma-surface2 p-6" data-testid="payout-dialog">
        <div className="mb-5 flex items-center justify-between">
          <h3 className="font-display text-2xl text-soma-on">Record Payout</h3>
          <button onClick={onClose} className="text-soma-muted hover:text-soma-on">
            <X size={22} />
          </button>
        </div>
        <p className="mb-4 text-sm text-soma-muted">To {practitioner.full_name}</p>
        <div className="space-y-3">
          <input placeholder="Amount (USD)" type="number" className={field} value={amount} data-testid="payout-amount" onChange={(e) => setAmount(e.target.value)} />
          <input placeholder="Note (optional)" className={field} value={note} data-testid="payout-note" onChange={(e) => setNote(e.target.value)} />
        </div>
        <button
          onClick={submit}
          disabled={busy}
          data-testid="payout-submit"
          className="mt-5 w-full rounded-xl bg-soma-brand py-3 font-semibold text-soma-surface hover:opacity-90 disabled:opacity-60"
        >
          {busy ? "Recording…" : "Record payout"}
        </button>
      </div>
    </div>
  );
}

export default function Practitioners() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [payoutFor, setPayoutFor] = useState(null);

  const load = () => {
    setLoading(true);
    api
      .get("/admin/practitioners")
      .then(setList)
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const toggleActive = async (p) => {
    try {
      await api.patch(`/admin/practitioners/${p.id}`, { is_active: !p.is_active });
      setList((ls) => ls.map((x) => (x.id === p.id ? { ...x, is_active: !x.is_active } : x)));
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Remove ${p.full_name}? This cannot be undone.`)) return;
    try {
      await api.del(`/admin/practitioners/${p.id}`);
      setList((ls) => ls.filter((x) => x.id !== p.id));
      toast.success("Practitioner removed");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="min-h-screen p-6 md:p-10">
      <header className="mb-8 flex items-center justify-between">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-soma-brand">// access &amp; payouts</p>
          <h1 className="mt-1 font-display text-4xl font-medium text-soma-on">Practitioners</h1>
        </div>
        <button
          onClick={() => setShowInvite(true)}
          data-testid="open-invite-button"
          className="flex items-center gap-2 rounded-xl bg-soma-brand px-4 py-3 text-sm font-semibold text-soma-surface hover:opacity-90"
        >
          <UserPlus size={18} /> Invite
        </button>
      </header>

      {loading ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-24 animate-pulse rounded-2xl bg-soma-surface2" />
          ))}
        </div>
      ) : list.length === 0 ? (
        <div className="rounded-2xl border border-soma-border bg-soma-surface2 p-16 text-center">
          <p className="text-soma-muted">No practitioners yet. Invite your first one.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {list.map((p) => (
            <div
              key={p.id}
              data-testid={`practitioner-row-${p.id}`}
              className="flex flex-col gap-4 rounded-2xl border border-soma-border bg-soma-surface2 p-5 md:flex-row md:items-center md:justify-between"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="truncate font-medium text-soma-on">{p.full_name}</p>
                  <span className="rounded-full bg-soma-surface3 px-2 py-0.5 text-[11px] text-soma-on2">
                    Type {p.account_type}
                  </span>
                </div>
                <p className="truncate text-sm text-soma-muted">{p.email}</p>
                <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-soma-on2">
                  <span
                    data-testid={`readings-month-${p.id}`}
                    className="rounded-full bg-soma-brand/15 px-2.5 py-1 font-semibold text-soma-brand"
                  >
                    {p.stats.readings_this_month ?? 0} this month
                  </span>
                  <span>{p.stats.readings} all-time</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => toggleActive(p)}
                  data-testid={`toggle-active-${p.id}`}
                  className={`rounded-lg px-3 py-2 text-xs font-medium ${
                    p.is_active
                      ? "bg-soma-success/25 text-soma-on"
                      : "bg-soma-surface3 text-soma-muted"
                  }`}
                >
                  {p.is_active ? "Active" : "Paused"}
                </button>
                <button
                  onClick={() => setPayoutFor(p)}
                  data-testid={`payout-${p.id}`}
                  className="rounded-lg bg-soma-surface3 p-2 text-soma-on2 hover:text-soma-brand"
                  title="Record payout"
                >
                  <Wallet size={18} />
                </button>
                <button
                  onClick={() => remove(p)}
                  data-testid={`delete-${p.id}`}
                  className="rounded-lg bg-soma-surface3 p-2 text-soma-on2 hover:text-red-400"
                  title="Remove"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showInvite && <InviteDialog onClose={() => setShowInvite(false)} onCreated={load} />}
      {payoutFor && <PayoutDialog practitioner={payoutFor} onClose={() => setPayoutFor(null)} onSaved={load} />}
    </div>
  );
}
